import { useContext } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { Braces } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { VarLabelContext } from "./var-label-context.js";
import { normalizeToken } from "./var-token-serialization.js";

/**
 * Inline chip for a `{{…}}` variable. Shows the human label resolved from
 * context so the token reads like a label, not code. An unknown token (a renamed
 * or removed variable) degrades to a muted chip showing its bare name — never
 * dropped, so the stored value stays intact and the author notices the gap.
 */
export function VarChip(props: NodeViewProps) {
  const token = String(props.node.attrs.token ?? "");
  const labels = useContext(VarLabelContext);
  const label = labels.get(normalizeToken(token));
  const known = label !== undefined;
  const bare = token.replace(/^\{\{\s*|\s*\}\}$/g, "");

  return (
    <NodeViewWrapper as="span" className="cc-var-chip-wrap">
      <span
        data-var-token={token}
        data-known={known ? "" : undefined}
        title={known ? label : `Unknown variable "${bare}"`}
        className={cn(
          "inline-flex select-none items-center gap-1 rounded px-1.5 py-0.5 align-baseline text-sm font-medium",
          known
            ? "bg-muted text-foreground"
            : "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
        )}
        contentEditable={false}
      >
        <Braces className="h-3 w-3 shrink-0 opacity-60" aria-hidden />
        <span>{known ? label : bare}</span>
      </span>
    </NodeViewWrapper>
  );
}
